import { Spinner } from "@chakra-ui/react";
import { CheckCircleIcon, WarningCircleIcon } from "@phosphor-icons/react";
import { FC, useState } from "react";
import { formatFullName } from "../../../utils/functions";
import {
  EVENT_RESULT_SKIP_OPTIONS,
  EVENT_RESULT_TYPES_OPTIONS,
} from "../../../constants/config";
import { EVENT_RESULT_TYPES } from "../../../enums/eventresults";
import { useEndpoints } from "../../../hooks/useEndpoints";
import { useForm } from "react-hook-form";
import { useToastAlert } from "../../../hooks/useToastAlert/useToastAlert";
import { addBaseUrl } from "../../../lib/DurationPicker/utils";
import { IEvent } from "../../../models/event";
import { IEventResult, IRpeEventResult } from "../../../models/eventResult";
import CustomAvatar from "../../../shared/CustomAvatar/CustomAvatar";
import CustomButton from "../../../shared/CustomButton/CustomButton";
import CustomTextInput from "../../../shared/CustomInput/CustomTextInput";
import CustomSelectInput from "../../../shared/CustomSelectInput/CustomSelectInput";
import { toastServerErrors } from "../../../shared/CustomFormV3/serverErrors";
import Typhography from "../../../shared/Typhography/Typhography";
import cx from "classnames";
import style from "./CompileEvent.module.scss";

export const CompileRpeEvent: FC<{
  event: IEvent;
  eventResults: IEventResult[];
  onClose: () => void;
}> = ({ event, eventResults, onClose }) => {
  const toastAlert = useToastAlert();
  const [isLoading, setIsLoading] = useState(false);
  const [statusUser, setStatusUser] = useState<
    Record<string, "success" | "error" | "loading">
  >({});
  const endpoints = useEndpoints();

  const { watch, setValue } = useForm({
    defaultValues: {
      records: event.userIds.map((user) => {
        const eventResult = eventResults.find(
          (i) => i.userId === user._id,
        ) as any as IRpeEventResult;
        return {
          user,
          _id: user._id,
          skipped: eventResult?.skipped,
          value: eventResult?.value,
          duration: eventResult?.duration ?? event.duration ?? "",
        };
      }),
    },
  });

  const records = watch("records");

  const [search, setSearch] = useState("");

  const filteredIndices = records.reduce<number[]>((acc, record, i) => {
    const fullName = `${record.user.firstName} ${record.user.lastName}`.toLowerCase();
    if (!search || fullName.includes(search.toLowerCase())) acc.push(i);
    return acc;
  }, []);

  const onChangeItem = (i: number, key: string, value: any) => {
    setValue(`records.${i}.${key}` as any, value);
  };

  const handleSave = async () => {
    // Filter out athletes with no value and no skip reason
    const validRecords = records.filter((user) => user.skipped || (user.value != null && user.value !== '' as any));
    const items = validRecords.map((user) => {
      // Minuti effettivi individuali. Campo vuoto:
      // - CREATE: non inviare duration (il backend usa la durata evento)
      // - PATCH: il backend mantiene la duration già salvata sul risultato,
      //   quindi per "resettare" un override serve reinviare esplicitamente
      //   la durata dell'evento.
      const durationNum = Number(user.duration);
      const hasDuration = Number.isFinite(durationNum) && durationNum > 0;
      const isExisting = eventResults.some((r) => r.userId === user._id);
      const eventDuration = Number(event.duration);
      const resetDuration =
        !hasDuration && isExisting && Number.isFinite(eventDuration) && eventDuration > 0
          ? { duration: eventDuration }
          : {};
      return {
        eventId: event._id,
        userId: user._id,
        type: EVENT_RESULT_TYPES.RPE,
        ...(!!user.skipped
          ? { skipped: user.skipped }
          : {
              value: Number(user.value),
              ...(hasDuration ? { duration: durationNum } : resetDuration),
            }),
      };
    });
    setIsLoading(true);
    try {
      const toUpdate: { id: string; body: any; userId: string }[] = [];
      const toCreate: any[] = [];
      const createIndexMap: number[] = [];

      items.forEach((item, i) => {
        const existing = eventResults.find((r) => r.userId === item.userId);
        if (existing) {
          toUpdate.push({ id: existing._id, body: item, userId: item.userId });
        } else {
          createIndexMap.push(i);
          toCreate.push(item);
        }
      });

      const patchResults = await Promise.all(
        toUpdate.map(async ({ id, body, userId }) => {
          try {
            setStatusUser((prev) => ({ ...prev, [userId]: "loading" }));
            await endpoints.patchEventResult(id, body);
            setStatusUser((prev) => ({ ...prev, [userId]: "success" }));
            return true;
          } catch {
            setStatusUser((prev) => ({ ...prev, [userId]: "error" }));
            return false;
          }
        })
      );

      let bulkSuccess = true;
      if (toCreate.length > 0) {
        toCreate.forEach((item) => setStatusUser((prev) => ({ ...prev, [item.userId]: "loading" })));
        const { data: bulkResults } = await endpoints.postEventResultBulk(toCreate);
        bulkResults.forEach((result) => {
          const recordIdx = createIndexMap[result.index];
          const userId = records[recordIdx]._id;
          setStatusUser((prev) => ({ ...prev, [userId]: result.success ? "success" : "error" }));
          if (!result.success) bulkSuccess = false;
        });
      }

      const hasErrors = patchResults.some((r) => !r) || !bulkSuccess;
      if (hasErrors) {
        toastAlert({ message: "Alcuni risultati non sono stati salvati", type: "error" });
      } else {
        toastAlert({ message: "Compilazione salvata con successo" });
      }
      onClose();
    } catch (error) {
      console.error(error);
      if (!toastServerErrors(error)) {
        toastAlert({
          message: "Alcuni risultati non sono stati salvati",
          type: "error",
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <div className="d-flex flex-column flex-1 overflow-auto gap-2">
        <div className={style.searchBar}>
          <CustomTextInput
            placeholder="Cerca atleta..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        {filteredIndices.map((i) => {
          const { user, value, skipped, duration } = records[i];
          return (
            <div key={user._id} className={style.row}>
              <div className={style.userCell}>
                <CustomAvatar
                  name={`${user.firstName[0]}${user.lastName[0]}`}
                  src={addBaseUrl(1, user.image?.image)}
                />
                <div className={style.userInfo}>
                  <Typhography size="bold-14" className={style.userName}>
                    {formatFullName(user.firstName, user.lastName)}
                  </Typhography>
                  {statusUser[user._id] === "success" && (
                    <div className={cx(style.statusBadge, style.success)}>
                      <CheckCircleIcon size={12} weight="fill" />
                      <Typhography size="regular-12">Salvato</Typhography>
                    </div>
                  )}
                  {statusUser[user._id] === "error" && (
                    <div className={cx(style.statusBadge, style.error)}>
                      <WarningCircleIcon size={12} weight="fill" />
                      <Typhography size="regular-12">Errore</Typhography>
                    </div>
                  )}
                  {statusUser[user._id] === "loading" && (
                    <Spinner size="xs" />
                  )}
                </div>
              </div>

              <div className={style.fieldsCell}>
                <div style={{ flex: 1, minWidth: 180 }}>
                  <CustomSelectInput
                    label="Motivazione assenza"
                    placeholder="Nessuna assenza"
                    options={EVENT_RESULT_SKIP_OPTIONS}
                    value={skipped}
                    clearOption={{ label: "Nessuna", value: undefined }}
                    onChange={(value) => onChangeItem(i, "skipped", value)}
                  />
                </div>
                <div style={{ width: 160 }}>
                  <CustomTextInput
                    disabled={!!skipped}
                    onValueNumberChange={(value) =>
                      onChangeItem(i, "value", value.valueAsNumber)
                    }
                    classNameInput="w-100"
                    max={10}
                    min={1}
                    label="RPE (1–10)"
                    value={value}
                    type="number"
                  />
                </div>
                <div className={style.minutesCell}>
                  <CustomTextInput
                    disabled={!!skipped}
                    onValueNumberChange={(value) =>
                      onChangeItem(i, "duration", value.valueAsNumber)
                    }
                    classNameInput="w-100"
                    max={600}
                    min={1}
                    label="Minuti"
                    value={duration}
                    type="number"
                  />
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div className={style.actionsFooter}>
        <CustomButton color="outline" onClick={onClose} disabled={isLoading}>
          Annulla
        </CustomButton>
        <CustomButton color="secondary" isLoading={isLoading} onClick={handleSave}>
          Conferma
        </CustomButton>
      </div>
    </>
  );
};
